// TourOverlay — the bottom editorial card + soft highlight ring.
//
// Design principles from the spec:
//   * The app remains the main character — the tour only points.
//   * No large scrim, no bright blue tour UI, no bouncing.
//   * Fade in 200–250 ms, ease-out, no overshoot.
//   * Respect prefers-reduced-motion (via CSS media query on .c-tour-*).
//   * The bottom card must respect safe-area insets on mobile Safari.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useTour } from "./TourProvider";
import { useCommons } from "../lib/store";

const TARGET_POLL_MS = 60;
const TARGET_POLL_MAX = 24; // ~1.4s of polling before we auto-skip

export default function TourOverlay() {
  const {
    active, step, total, currentStep, waitingForCommit, waitingForInteraction,
    next, back, skip, close, restartFresh, restartInPlace,
    markInteractionDone,
  } = useTour();
  const { data, user } = useCommons();

  const [targetRect, setTargetRect] = useState(null);
  const [cardVisible, setCardVisible] = useState(false);
  const cardRef = useRef(null);

  const resolveSelector = useCallback(() => {
    if (!currentStep) return null;
    try {
      return typeof currentStep.targetSelector === "function"
        ? currentStep.targetSelector({ data, user })
        : currentStep.targetSelector;
    } catch (_) {
      return null;
    }
  }, [currentStep, data, user]);

  // Locate the current step's target — polls a few times because a route
  // change may need a beat for React to render + effects to settle.
  useEffect(() => {
    if (!active || !currentStep) { setTargetRect(null); setCardVisible(false); return; }
    let cancelled = false;
    let tries = 0;
    setCardVisible(false);

    // Apply the step's scroll intent immediately so even a target-less
    // finale step returns the page to a graceful position.
    if (currentStep.scroll === "top") {
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) { window.scrollTo(0, 0); }
    } else if (currentStep.scroll === "bottom") {
      try { window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); } catch (_) {}
    }

    const findTarget = () => {
      if (cancelled) return;
      const selector = resolveSelector();
      const el = selector ? document.querySelector(selector) : null;

      if (!el) {
        tries += 1;
        if (currentStep.isFinale) {
          setTargetRect(null);
          setCardVisible(true);
          return;
        }
        if (tries >= TARGET_POLL_MAX) {
          // Graceful skip — do not point at empty space.
          setTargetRect(null);
          setCardVisible(true);
          return;
        }
        setTimeout(findTarget, TARGET_POLL_MS);
        return;
      }

      // Gently scroll target into view.
      if (currentStep.scroll === "target") {
        try {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch (_) { el.scrollIntoView(); }
      }

      setTimeout(() => {
        if (cancelled) return;
        const r = el.getBoundingClientRect();
        setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        setCardVisible(true);
      }, currentStep.scroll === "target" || currentStep.scroll === "top" || currentStep.scroll === "bottom" ? 400 : 80);
    };
    findTarget();

    return () => { cancelled = true; };
  }, [active, step, currentStep, resolveSelector]);

  // Re-measure target on scroll + resize so the highlight ring follows.
  useEffect(() => {
    if (!active || !currentStep || !targetRect) return;
    const onChange = () => {
      const selector = resolveSelector();
      const el = selector ? document.querySelector(selector) : null;
      if (el) {
        const r = el.getBoundingClientRect();
        setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener("scroll", onChange, { passive: true });
    window.addEventListener("resize", onChange);
    return () => {
      window.removeEventListener("scroll", onChange);
      window.removeEventListener("resize", onChange);
    };
  }, [active, currentStep, targetRect, resolveSelector]);

  // Interaction gate: for steps flagged `interactive: true` the visitor
  // must tap the halo target itself (an actual Commons control). We
  // listen once on the resolved target; on click we let the target's
  // own click handler perform the real navigation, then advance the
  // tour. No preventDefault — the visitor's tap IS the navigation.
  useEffect(() => {
    if (!active || !currentStep?.interactive || !targetRect) return;
    const selector = resolveSelector();
    const el = selector ? document.querySelector(selector) : null;
    if (!el) return;
    const onClick = () => { markInteractionDone(); };
    el.addEventListener("click", onClick, { once: true });
    return () => { el.removeEventListener("click", onClick); };
  }, [active, currentStep, targetRect, resolveSelector, markInteractionDone]);

  // Escape closes the tour.
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close]);

  // Move focus into the card on step change for accessibility.
  useEffect(() => {
    if (!active || !cardVisible) return;
    const t = setTimeout(() => {
      cardRef.current?.querySelector('[data-tour-focus]')?.focus?.();
    }, 60);
    return () => clearTimeout(t);
  }, [active, cardVisible, step]);

  const stepNumber = step + 1;
  const isFinale = !!currentStep?.isFinale;
  const primaryLabel = useMemo(() => {
    // "try-it" stop keeps the softer "I'll commit later" — the required
    // action there is a real financial-adjacent commitment.
    if (currentStep?.id === "try-it" && waitingForInteraction) return "I'll commit later";
    // Generic interactive stops: the visitor must tap the highlighted
    // real Commons control. Offer a plain skip-this-step escape hatch.
    if (waitingForInteraction) return currentStep?.fallbackCta || "Skip this step";
    if (isFinale) return currentStep?.primaryCta || "Start exploring";
    if (stepNumber === total) return "Done";
    return "Continue";
  }, [waitingForInteraction, isFinale, currentStep, stepNumber, total]);

  const onPrimary = useCallback(() => {
    if (isFinale) { close(); return; }
    next();
  }, [isFinale, close, next]);

  const onRestartFresh = useCallback(async () => {
    if (typeof window !== "undefined") {
      const ok = window.confirm(
        "Restart the tour with a fresh demo? This clears your commitments and reseeds the demo."
      );
      if (!ok) return;
    }
    await restartFresh();
  }, [restartFresh]);

  if (!active || !currentStep) return null;

  return (
    <div className="c-tour-root" role="dialog" aria-labelledby="c-tour-title" aria-modal="false">
      {targetRect ? (
        <div
          className="c-tour-halo"
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
          }}
          aria-hidden="true"
        />
      ) : null}

      <div
        className={`c-tour-card ${cardVisible ? "is-visible" : ""}`}
        ref={cardRef}
        data-testid="tour-card"
      >
        <div className="c-tour-head">
          <div className="c-tour-progress" aria-live="polite">
            Stop {stepNumber} of {total}
          </div>
          <button
            className="c-tour-close"
            aria-label="Close guided tour"
            onClick={close}
            data-testid="tour-close"
          >
            <X size={18} />
          </button>
        </div>

        <h2 id="c-tour-title" className="c-tour-title" tabIndex={-1} data-tour-focus>
          {currentStep.title}
        </h2>
        <div className="c-tour-body">
          {currentStep.body.split("\n").map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>

        <div className="c-tour-foot">
          {isFinale ? (
            <>
              <button
                className="c-btn c-btn-ghost"
                onClick={onRestartFresh}
                data-testid="tour-restart-fresh"
              >
                {currentStep.secondaryCta || "Restart with a fresh demo"}
              </button>
              <button
                className="c-btn c-btn-primary"
                onClick={onPrimary}
                data-testid="tour-primary-btn"
              >
                {primaryLabel}
              </button>
            </>
          ) : (
            <>
              <button
                className="c-tour-secondary"
                onClick={back}
                disabled={step === 0}
                data-testid="tour-back-btn"
              >
                <ChevronLeft size={16} /> Back
              </button>
              <button
                className="c-tour-secondary"
                onClick={skip}
                data-testid="tour-skip-btn"
              >
                Skip
              </button>
              <button
                className="c-btn c-btn-primary c-tour-primary"
                onClick={onPrimary}
                data-testid="tour-primary-btn"
              >
                {primaryLabel} {!waitingForInteraction ? <ChevronRight size={16} /> : null}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
